import { CognitoUser } from 'amazon-cognito-identity-js';
import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountContext } from '@/Accounts';
import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';
import { getCognitoUserPool } from '@/services/auth/userPool';
import type { AuthProps, CognitoAuthResponse } from '@/types/auth';

export enum LoginStage {
  LOGIN = 0,
  FORGOT_PASSWORD = 1,
  RESET_PASSWORD = 2,
  SUCCESS = 3,
}

interface UseLoginProps extends Partial<AuthProps> {}

export const useLogin = ({ setIsLoggedIn, setLoading, loading }: UseLoginProps) => {
  const [error, setError] = useState<string | boolean>(false);
  const [stage, setStage] = useState<LoginStage>(LoginStage.LOGIN);
  const [user, setUser] = useState<CognitoUser | undefined>();

  const navigate = useNavigate();
  const location = useLocation();
  const { authenticate } = useContext(AccountContext);

  const from = location.state?.from?.pathname || '/home';

  const handleLogin = async (email: string, password: string) => {
    if (setLoading) setLoading(true);
    try {
      if (email === '' || password === '') {
        setError('Please provide credentials');
        if (setLoading) setLoading(false);
        return;
      }

      const data: CognitoAuthResponse = await authenticate(email, password);

      const response = await fetch(`${BASE_URL}${ENDPOINTS.userSettings}`, {
        headers: {
          Authorization: `Bearer ${data.idToken.jwtToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const settingsResponse = await response.json();

      if (setLoading) setLoading(false);

      const authData = {
        ...data,
        config: {
          ...settingsResponse.settings,
          subscription_type: settingsResponse.subscription_type,
          expiration_timestamp: settingsResponse.expiration_timestamp,
          customer_id: settingsResponse.customer_id,
        },
      };

      if (settingsResponse.subscription_type === 'free') {
        const expirationTimestamp = new Date(settingsResponse.expiration_timestamp * 1000);
        if (expirationTimestamp.getTime() < Date.now()) {
          if (setIsLoggedIn) setIsLoggedIn(null);
          setError('Your trial account has expired');
        } else {
          if (setIsLoggedIn) setIsLoggedIn(authData);
          navigate(from, { replace: true });
        }
      } else {
        if (setIsLoggedIn) setIsLoggedIn(authData);
        navigate(from, { replace: true });
      }
    } catch (_err: unknown) {
      setError('Invalid Credentials');
      if (setLoading) setLoading(false);
    }
  };

  const handleSendCode = (email: string) => {
    const cognitoUser = new CognitoUser({
      Username: email.toLowerCase(),
      Pool: getCognitoUserPool(),
    });
    setUser(cognitoUser);

    cognitoUser.forgotPassword({
      onSuccess: (_data) => {
        // Password reset initiated
      },
      onFailure: (_err) => {
        // Error handled silently in original code
      },
      inputVerificationCode: (_data) => {
        setStage(LoginStage.RESET_PASSWORD);
      },
    });
  };

  const handleResetPassword = (code: string, newPassword: string, confirmedNewPassword: string) => {
    if (newPassword !== confirmedNewPassword) {
      setError(true);
      return;
    }

    user?.confirmPassword(code, newPassword, {
      onSuccess: (_data) => {
        setStage(LoginStage.SUCCESS);
      },
      onFailure: (err) => {
        setError(true);
        console.error('onFailure:', err);
      },
    });
  };

  return {
    stage,
    setStage,
    error,
    setError,
    loading,
    handleLogin,
    handleSendCode,
    handleResetPassword,
  };
};
